
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

type ModuleType = {
  module_number: number;
};

type AddModuleButtonProps = {
  courseId: string;
  modules?: ModuleType[];
  className?: string;
};

export function AddModuleButton({ courseId, modules, className }: AddModuleButtonProps) {
  const queryClient = useQueryClient();
  const { isAdmin } = useAdminAuth();

  const handleAddModule = async () => {
    if (!isAdmin) {
      toast.error('You must be an admin to add modules');
      return;
    }
    
    try {
      const lastModule = modules?.reduce((max, module) => 
        module.module_number > max.module_number ? module : max, 
        { module_number: 0 } as ModuleType
      );

      const nextModuleNumber = (lastModule?.module_number || 0) + 1;
      console.log(`Adding new module with number ${nextModuleNumber} for course ID: ${courseId}`);
      
      // Validate courseId to ensure it's not empty or undefined
      if (!courseId) {
        toast.error('Invalid course ID');
        return;
      }

      const newModule = {
        course_id: courseId,
        module_number: nextModuleNumber,
        part_number: 1,
        title: 'New Module',
        duration_minutes: 30,
        content: '',
        is_exam: false,
      };

      console.log('Inserting new module with data:', newModule);
      
      const { error, data } = await supabase
        .from('course_modules')
        .insert(newModule)
        .select();

      if (error) {
        console.error('Supabase error adding module:', error);
        toast.error(`Failed to add new module: ${error.message}`);
        return;
      }

      console.log('New module added:', data);
      toast.success('New module added successfully');
      
      // Force refresh the modulesList query
      await queryClient.invalidateQueries({ queryKey: ['courseModules', courseId] });
      
      // Add a small delay to ensure the database has time to update
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['courseModules', courseId] });
      }, 500);
    } catch (error) {
      console.error('Error adding new module:', error);
      toast.error('Failed to add new module');
    }
  };

  return (
    <Button
      variant="pill"
      onClick={handleAddModule}
      className={className}
    >
      Add Module
    </Button>
  );
}
